<template>
  <div>
    <!-- Summary Cards -->
    <div class="row" v-if="summary">
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-aqua">
          <div class="inner">
            <h3>{{ summary.totalShippingLines }}</h3>
            <p>Total Shipping Lines</p>
          </div>
          <div class="icon">
            <i class="fa fa-ship"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-green">
          <div class="inner">
            <h3>{{ formatCurrency(summary.totalInvoices) }}</h3>
            <p>Total Revenue</p>
          </div>
          <div class="icon">
            <i class="fa fa-money"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-yellow">
          <div class="inner">
            <h3>{{ formatCurrency(summary.totalProfit) }}</h3>
            <p>Total Profit</p>
          </div>
          <div class="icon">
            <i class="fa fa-line-chart"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
      <div class="col-lg-3 col-xs-6">
        <div class="small-box bg-red">
          <div class="inner">
            <h3>{{ summary.totalJobs }}</h3>
            <p>Total Jobs</p>
          </div>
          <div class="icon">
            <i class="fa fa-file-text"></i>
          </div>
          <a href="#" class="small-box-footer">&nbsp;</a>
        </div>
      </div>
    </div>

    <!-- Charts Row -->
    <div class="row">
      <!-- Top Shipping Lines by Profit -->
      <div class="col-md-6">
        <div class="box box-primary">
          <div class="box-header with-border">
            <h3 class="box-title">Top 10 Shipping Lines by Profit</h3>
          </div>
          <div class="box-body">
            <apexchart
              type="bar"
              height="350"
              :options="topShippingLinesChartData.options"
              :series="topShippingLinesChartData.series"
              v-if="records.length > 0"
            ></apexchart>
            <div v-else style="height: 350px; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
              <div style="text-align: center; color: #666;">
                <i class="fa fa-bar-chart fa-2x"></i>
                <p style="margin-top: 10px;">No data available</p>
                <small>Please load data to view charts</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Shipping Line Performance Distribution -->
      <div class="col-md-6">
        <div class="box box-warning">
          <div class="box-header with-border">
            <h3 class="box-title">Shipping Line Performance Distribution</h3>
          </div>
          <div class="box-body">
            <apexchart
              type="donut"
              height="350"
              :options="shippingLinePerformanceChartData.options"
              :series="shippingLinePerformanceChartData.series"
              v-if="records.length > 0"
            ></apexchart>
            <div v-else style="height: 350px; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 4px; background: #f9f9f9;">
              <div style="text-align: center; color: #666;">
                <i class="fa fa-pie-chart fa-2x"></i>
                <p style="margin-top: 10px;">No data available</p>
                <small>Please load data to view charts</small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="box box-default">
      <div class="box-header with-border">
        <h3 class="box-title">Shipping Line-wise Growth Data</h3>
        <div class="box-tools pull-right">
          <button type="button" class="btn btn-success btn-sm" @click="exportToExcel">
            <i class="fa fa-download"></i> Export Excel
          </button>
        </div>
      </div>
      <div class="box-body">
        <b-table
          :items="records"
          :fields="tableFields"
          :tbody-tr-class="getRowClass"
          striped
          bordered
          hover
          responsive="sm"
          class="text-center"
        >
          <template slot="sn" slot-scope="data">
            {{ ((currentPage - 1) * perPage) + data.index + 1 }}
          </template>

          <template slot="shippingLineId" slot-scope="data">
            <span class="text-center d-block">{{ data.value }}</span>
          </template>

          <template slot="shippingLineName" slot-scope="data">
            <span class="text-left d-block">{{ data.value }}</span>
          </template>

          <template slot="totalJobs" slot-scope="data">
            <span class="text-center d-block">{{ data.value }}</span>
          </template>

          <template slot="totalInvoices" slot-scope="data">
            <span class="text-right d-block">{{ formatCurrency(data.value) }}</span>
          </template>

          <template slot="totalExpenses" slot-scope="data">
            <span class="text-right d-block">{{ formatCurrency(data.value) }}</span>
          </template>

          <template slot="totalProfit" slot-scope="data">
            <span class="text-right d-block" :class="getProfitClass(data.value)">
              {{ formatCurrency(data.value) }}
            </span>
          </template>

          <template slot="avgInvoice" slot-scope="data">
            <span class="text-right d-block">{{ formatCurrency(data.value) }}</span>
          </template>

          <template slot="avgProfit" slot-scope="data">
            <span class="text-right d-block" :class="getProfitClass(data.value)">
              {{ formatCurrency(data.value) }}
            </span>
          </template>

          <template slot="profitPercentage" slot-scope="data">
            <span class="text-right d-block" :class="getPercentageClass(data.value)">
              {{ data.value.toFixed(2) }}%
            </span>
          </template>

          <template slot="status" slot-scope="data">
            <span class="label" :class="getStatusClass(data.value)">
              {{ data.value }}
            </span>
          </template>

          <template slot="bottom-row" v-if="summary">
            <td colspan="3" class="text-center font-weight-bold bg-light">TOTAL</td>
            <td class="text-center font-weight-bold bg-light">{{ summary.totalJobs }}</td>
            <td class="text-right font-weight-bold bg-light">{{ formatCurrency(summary.totalInvoices) }}</td>
            <td class="text-right font-weight-bold bg-light">{{ formatCurrency(summary.totalExpenses) }}</td>
            <td class="text-right font-weight-bold bg-light" :class="getProfitClass(summary.totalProfit)">
              {{ formatCurrency(summary.totalProfit) }}
            </td>
            <td colspan="4" class="bg-light"></td>
          </template>
        </b-table>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="totalRows > 0">
          <div class="d-flex align-items-center">
            <label class="mr-2 mb-0">Show:</label>
            <b-form-select
              v-model="perPage"
              :options="pageOptions"
              @change="handlePerPageChange"
              size="sm"
              class="w-auto"
            ></b-form-select>
            <span class="ml-2 text-muted">entries</span>
          </div>

          <b-pagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            @change="changePage"
            size="sm"
            class="mb-0"
            first-number
            last-number
          ></b-pagination>

          <div class="text-muted small">
            Showing {{ records.length ? ((currentPage - 1) * perPage + 1) : 0 }} to {{ Math.min(((currentPage - 1) * perPage) + records.length, totalRows) }} of {{ totalRows }} entries
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GrowthDashboardService from '../../../services/GrowthDashboardService';
import VueApexCharts from 'vue-apexcharts';
import * as XLSX from 'xlsx';
// Chart functionality temporarily disabled
// import Chart from 'chart.js';

export default {
  name: 'ShippingLineWiseGrowthComponent',
  components: {
    apexchart: VueApexCharts
  },
  props: {
    filters: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      records: [],
      summary: null,
      // Pagination
      currentPage: 1,
      perPage: 25,
      pageOptions: [25, 50, 100, 200],
      totalRows: 0,
      pagination: null,
      sortBy: null,
      sortDesc: false,
      sortDirection: 'asc',
      filter: null
    };
  },

  computed: {
    // Chart data for Top Shipping Lines Horizontal Bar Chart
    topShippingLinesChartData() {
      if (!this.records || this.records.length === 0) {
        return {
          series: [],
          options: {}
        };
      }

      // Sort shipping lines by total profit and take top 10
      const topShippingLines = [...this.records]
        .sort((a, b) => (b.totalProfit || 0) - (a.totalProfit || 0))
        .slice(0, 10);

      return {
        series: [{
          name: 'Total Profit',
          data: topShippingLines.map(line => parseFloat(line.totalProfit) || 0)
        }],
        options: {
          chart: {
            type: 'bar',
            height: 350,
            toolbar: {
              show: true
            }
          },
          plotOptions: {
            bar: {
              horizontal: true,
              columnWidth: '60%',
              borderRadius: 4
            }
          },
          dataLabels: {
            enabled: true,
            formatter: function(value) {
              return '₹' + (value / 100000).toFixed(0) + 'L';
            }
          },
          xaxis: {
            categories: topShippingLines.map(line => line.shippingLine || 'Unknown'),
            title: {
              text: 'Profit Amount (₹)'
            },
            labels: {
              formatter: function(value) {
                return '₹' + (value / 100000).toFixed(0) + 'L';
              }
            }
          },
          yaxis: {
            title: {
              text: 'Shipping Lines'
            }
          },
          colors: ['#00E396'],
          tooltip: {
            y: {
              formatter: function(value) {
                return '₹' + value.toLocaleString('en-IN');
              }
            }
          }
        }
      };
    },

    // Chart data for Shipping Line Performance Distribution Donut Chart
    shippingLinePerformanceChartData() {
      if (!this.records || this.records.length === 0) {
        return {
          series: [],
          options: {}
        };
      }

      const profitableLines = this.records.filter(line => (line.totalProfit || 0) > 0).length;
      const lossLines = this.records.filter(line => (line.totalProfit || 0) < 0).length;
      const breakEvenLines = this.records.filter(line => (line.totalProfit || 0) === 0).length;

      return {
        series: [profitableLines, lossLines, breakEvenLines],
        options: {
          chart: {
            type: 'donut',
            height: 350
          },
          labels: ['Profitable Lines', 'Loss Making Lines', 'Break Even Lines'],
          colors: ['#00E396', '#FF4560', '#FEB019'],
          legend: {
            position: 'bottom'
          },
          plotOptions: {
            pie: {
              donut: {
                size: '65%'
              }
            }
          },
          tooltip: {
            y: {
              formatter: function(value) {
                return value + ' shipping lines';
              }
            }
          },
          responsive: [{
            breakpoint: 480,
            options: {
              chart: {
                width: 200
              },
              legend: {
                position: 'bottom'
              }
            }
          }]
        }
      };
    },

    tableFields() {
      return [
        { key: 'sn', label: 'SN', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'shippingLineId', label: 'Shipping Line ID', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'shippingLineName', label: 'Shipping Line Name', thClass: 'text-center', tdClass: 'text-left' },
        { key: 'totalJobs', label: 'Total Jobs', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totalInvoices', label: 'Total Invoices', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totalExpenses', label: 'Total Expenses', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'totalProfit', label: 'Total Profit', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'avgInvoice', label: 'Avg Invoice', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'avgProfit', label: 'Avg Profit', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'profitPercentage', label: 'Profit %', thClass: 'text-center', tdClass: 'text-center' },
        { key: 'status', label: 'Status', thClass: 'text-center', tdClass: 'text-center' }
      ];
    },

  },
  watch: {
    filters: {
      handler() {
        this.currentPage = 1;
        this.loadData(1);
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    notifyUser({ type = 'info', title = '', text = '' } = {}) {
      if (this.$bvToast) {
        const variantMap = { info: 'info', success: 'success', warning: 'warning', error: 'danger' };
        this.$bvToast.toast(text, {
          title: title || 'Info',
          variant: variantMap[type] || 'info',
          solid: true
        });
        return;
      }
      if (this.$notify) {
        this.$notify({ type, title: title || 'Info', text });
        return;
      }
      if (type === 'error') console.error(title, text);
      else console.log(title, text);
    },
    async fetchAllRecordsForExport() {
      const baseParams = {};
      Object.keys(this.filters).forEach(key => {
        if (this.filters[key] !== null && this.filters[key] !== '') {
          baseParams[key] = this.filters[key];
        }
      });

      const exportPageSize = 500;
      const all = [];

      const firstRes = await GrowthDashboardService.getShippingLineWiseGrowthData(
        Object.assign({}, baseParams, {
          page: 1,
          pageSize: exportPageSize
        })
      );

      if (!firstRes.data || !firstRes.data.success) return [];

      const firstData = firstRes.data.data || {};
      const firstRecords = Array.isArray(firstData.records) ? firstData.records : [];
      all.push(...firstRecords);

      const pagination = firstData.pagination || {};
      const totalPages = Number(pagination.totalPages || 1);

      for (let page = 2; page <= totalPages; page++) {
        const res = await GrowthDashboardService.getShippingLineWiseGrowthData(
          Object.assign({}, baseParams, {
            page,
            pageSize: exportPageSize
          })
        );
        if (!res.data || !res.data.success) break;
        const data = res.data.data || {};
        const records = Array.isArray(data.records) ? data.records : [];
        all.push(...records);
      }

      return all;
    },
    async loadData(page = this.currentPage) {
      try {
        this.$emit('loading', true);

        const params = {
          page,
          pageSize: this.perPage
        };
        Object.keys(this.filters).forEach(key => {
          if (this.filters[key] !== null && this.filters[key] !== '') {
            params[key] = this.filters[key];
          }
        });

        const response = await GrowthDashboardService.getShippingLineWiseGrowthData(params);

        if (response.data.success) {
          const responseData = response.data.data || {};
          this.records = Array.isArray(responseData.records) ? responseData.records : [];
          this.summary = responseData.summary || null;

          const pagination = responseData.pagination || {};
          const totalCount = Number(pagination.totalCount || this.records.length || 0);
          const currentPage = Number(pagination.currentPage || page || 1);
          const pageSize = Number(pagination.pageSize || this.perPage || 50);
          const totalPages = Number(pagination.totalPages || Math.ceil(totalCount / pageSize) || 1);

          this.totalRows = totalCount;
          this.currentPage = currentPage;
          this.pagination = {
            currentPage,
            totalPages,
            pageSize,
            totalCount
          };
          // Chart functionality temporarily disabled
          // this.$nextTick(() => {
          //   this.renderCharts();
          // });
        }
      } catch (error) {
        console.error('Error loading shipping line-wise growth data:', error);
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Failed to load shipping line-wise growth data'
        });
      } finally {
        this.$emit('loading', false);
      }
    },
    changePage(page) {
      if (!page || page < 1) return;
      if (page === this.currentPage) return;
      this.loadData(page);
    },
    handlePerPageChange() {
      this.currentPage = 1;
      this.loadData(1);
    },

    // Chart functionality temporarily disabled
    renderCharts() {
      // Charts will be implemented when Chart.js is properly configured
      console.log('Chart rendering temporarily disabled');
    },

    renderTopShippingLinesChart() {
      // Chart functionality temporarily disabled
    },

    renderShippingLinePerformanceChart() {
      // Chart functionality temporarily disabled
    },

    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '₹0';
      return '₹' + Math.abs(amount).toLocaleString('en-IN');
    },

    getRowClass(record) {
      if (record.status === 'Loss') return 'danger';
      if (record.status === 'Profit') return 'success';
      return '';
    },

    getProfitClass(amount) {
      return amount >= 0 ? 'text-success' : 'text-danger';
    },

    getPercentageClass(percentage) {
      if (percentage > 0) return 'text-success';
      if (percentage < 0) return 'text-danger';
      return 'text-muted';
    },

    getStatusClass(status) {
      switch (status) {
        case 'Profit': return 'label-success';
        case 'Loss': return 'label-danger';
        default: return 'label-default';
      }
    },

    async exportToExcel() {
      try {
        this.notifyUser({
          type: 'info',
          title: 'Export',
          text: 'Preparing Excel export. Please wait...'
        });

        const allRecords = await this.fetchAllRecordsForExport();
        if (!allRecords || allRecords.length === 0) {
          this.notifyUser({
            type: 'warning',
            title: 'Export',
            text: 'No shipping line-wise data available to export.'
          });
          return;
        }

        const excelRows = allRecords.map((record, index) => ({
          SN: index + 1,
          'Shipping Line ID': record.shippingLineId || '',
          'Shipping Line Name': record.shippingLineName || record.shippingLine || '',
          'Total Jobs': record.totalJobs || 0,
          'Total Invoices': record.totalInvoices || 0,
          'Total Expenses': record.totalExpenses || 0,
          'Total Profit': record.totalProfit || 0,
          'Avg Invoice': record.avgInvoice || 0,
          'Avg Profit': record.avgProfit || 0,
          'Profit %': record.profitPercentage || 0,
          Status: record.status || ''
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Shipping Growth');

        const timestamp = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `Shipping_Line_Wise_Growth_${timestamp}.xlsx`);
      } catch (error) {
        console.error('Error exporting shipping line-wise data:', error);
        this.notifyUser({
          type: 'error',
          title: 'Error',
          text: 'Failed to export shipping line-wise data'
        });
      }
    }
  },

  mounted() {
    this.loadData(1);
  },

  beforeDestroy() {
    // Chart functionality temporarily disabled
    // No chart cleanup needed
  }
};
</script>

<style scoped>
.table-responsive {
  overflow-x: auto;
}

.bg-gray {
  background-color: #f5f5f5 !important;
}

.label {
  display: inline-block;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: bold;
  line-height: 1;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  vertical-align: baseline;
  border-radius: 4px;
}

.label-success {
  background-color: #5cb85c;
}

.label-danger {
  background-color: #d9534f;
}

.label-default {
  background-color: #999;
}

.small-box {
  margin-bottom: 15px;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.text-success {
  color: #3c763d !important;
}

.text-danger {
  color: #a94442 !important;
}

.text-muted {
  color: #999 !important;
}
</style>



// WEBPACK FOOTER //
// ShippingLineWiseGrowthComponent.vue?e8ac54a2